import { useState, useMemo, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router';
import { useSelector } from 'react-redux';
import { IconButtonWithText, MixerLoader, Modal, YesNo } from '../../components/Common';
import { useHttpArchiveProject, useHttpGetProjectWithClientUserBranch, useHttpUnarchiveProject } from '../../hooks';
import useHttpDeleteProject from '../../hooks/project/projectApi/useHttpDeleteProject';
import PageNotFound from '../PageNotFound';
import { ProjectStatusBadge } from '../Projects/TableView';
import { ProjectTabs, ProjectTabType, ProjectOverviewTab, ProjectTimesheetTab, ProjectAuditTrailTab } from '../../components/Projects';
import { selectAuth } from '../../store/slices/auth.slice';
import { ProjectWithAssigneesDto } from '../../commonTypes/acs';
import { ProjectAssigneeDto } from '../../commonTypes/project.types';
import ProjectFilesTab from '../../components/Projects/ProjectFilesTab';
import { useResizeListener } from '../../hooks/windowEvents/useResizeListener';
import { useTranslation } from '../../hooks/useTranslation';
import NotAvailableOnMobile from '../../components/Common/NotAvailableOnMobile';
import { checkIfAdminOrOwner, checkIfAdminOrOwnerOrPm, isAuthorized } from '../../api/utils/roleUtils';
import { AUTHORIZED_ADMIN_OWNER, AUTHORIZED_ORDINARY_TEAM_LEAD_PM_HR } from '../../constants';
import ChangeProjectStatus from '../../components/modalContent/ChangeProjectStatus';
import { ProjectStatus } from '@cyberlabrs/helpers';

interface ProjectHeaderProps {
    projectName: string;
    clientName: string;
    projectStatus?: string;
    projectId: string;
    clientId: string;
    canEdit: boolean;
    onDeleteClick: () => void;
    onArchiveToggleClick: () => void;
    archivedMode: boolean;
    onStatusBadgeClick?: () => void;
}

const ProjectHeader = ({
    projectName,
    clientName,
    projectStatus,
    projectId,
    clientId,
    canEdit,
    onDeleteClick,
    onArchiveToggleClick,
    archivedMode,
    onStatusBadgeClick,
}: ProjectHeaderProps) => {
    const navigate = useNavigate();

    const handleClientClick = () => {
        navigate(`/clients/${clientId}`);
    };

    return (
        <div className="project-header">
            <button className="project-header__back" onClick={() => navigate('/projects')}>
                <img src="/svg/arrow-left-gray.svg" alt="Back" />
                BACK TO PROJECT LIST
            </button>

            <div className="project-header__content">
                <div className="project-header__info">
                    <h1>{projectName}</h1>
                    <div className="project-header__meta">
                        <p
                            className={`project-header__client ${canEdit ? 'project-header__client--clickable' : ''}`}
                            onClick={canEdit ? handleClientClick : undefined}
                            style={canEdit ? { cursor: 'pointer' } : { cursor: 'default' }}
                        >
                            {clientName}
                        </p>
                        {projectStatus && (
                            <div
                                onClick={canEdit ? onStatusBadgeClick : undefined}
                                style={{ display: 'inline-flex', cursor: canEdit ? 'pointer' : 'default' }}
                            >
                                <ProjectStatusBadge status={projectStatus} />
                            </div>
                        )}
                    </div>
                </div>

                {canEdit && (
                    <div className="project-header__actions">
                        <IconButtonWithText
                            onClick={onArchiveToggleClick}
                            text={archivedMode ? 'Restore project' : 'Archive project'}
                            iconSrc={archivedMode ? '/svg/unarchive.svg' : '/svg/archive.svg'}
                            variant="text"
                            textColour="gray"
                        />
                        <IconButtonWithText
                            onClick={onDeleteClick}
                            text={'Delete project'}
                            iconSrc="/svg/trash.svg"
                            variant="text"
                            textColour="gray"
                        />
                        <IconButtonWithText
                            onClick={() => navigate(`/projects/${projectId}/edit`)}
                            text={'Edit project'}
                            iconSrc="/svg/pen.svg"
                            variant="text"
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

interface ProjectPageProps {
    projectId: string;
}

const ProjectPage = ({ projectId }: ProjectPageProps) => {
    const navigate = useNavigate();
    const { t } = useTranslation('projects');
    const authUser = useSelector(selectAuth);
    const [activeTab, setActiveTab] = useState<ProjectTabType>('overview');
    const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

    const location = useLocation();

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        if (params.get('refresh') === 'true') {
            refreshProject();
        }
    }, [location]);

    const canEdit = checkIfAdminOrOwnerOrPm(authUser.role.name);
    const canSeeAllTabs = isAuthorized(authUser.role.name);
    const ownerTabs = checkIfAdminOrOwner(authUser.role.name);
    const showOnlyOwnData = AUTHORIZED_ORDINARY_TEAM_LEAD_PM_HR.includes(authUser.role.name);
    const canManageSubmissions = AUTHORIZED_ADMIN_OWNER.includes(authUser.role.name);
    const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
    const [isChangeStatusOpen, setIsChangeStatusOpen] = useState(false);

    const { isReady, project, getProjects } = useHttpGetProjectWithClientUserBranch(projectId);
    const { exec: archiveProject } = useHttpArchiveProject();
    const { exec: unarchiveProject } = useHttpUnarchiveProject();

    const { execDelete } = useHttpDeleteProject(() => {
        navigate('/projects');
    });

    const refreshProject = canEdit ? () => getProjects(undefined, true) : () => {};

    const currentUserAssignee = useMemo(() => {
        if (showOnlyOwnData && project) {
            return (project as ProjectWithAssigneesDto).projectAssignees?.find((a: ProjectAssigneeDto) => a.userSub === authUser.userSub);
        }
        return null;
    }, [showOnlyOwnData, project, authUser.userSub]);

    const currentUserMemberData = useMemo(() => {
        if (!currentUserAssignee) {
            return {
                name:
                    authUser.profile?.givenName && authUser.profile?.familyName
                        ? `${authUser.profile.givenName} ${authUser.profile.familyName}`
                        : 'Unknown',
                role: authUser.profile?.title || '',
                imageUrl: authUser.profile?.imageUrl,
                rate: null,
            };
        }
        return {
            name:
                `${currentUserAssignee.givenName || authUser.profile?.givenName || ''} ${
                    currentUserAssignee.familyName || authUser.profile?.familyName || ''
                }`.trim() || 'Unknown',
            role: currentUserAssignee.title || authUser.profile?.title || '',
            imageUrl: currentUserAssignee.imageUrl || authUser.profile?.imageUrl,
            rate: currentUserAssignee.rate ?? null,
        };
    }, [currentUserAssignee, authUser]);

    const visibleTabs = useMemo(() => {
        const tabs: ProjectTabType[] = ['overview', 'timesheet'];
        if (ownerTabs) {
            tabs.splice(1, 0, 'audit-trail', 'files');
        }
        return tabs;
    }, [ownerTabs]);

    if (!isReady) {
        return <MixerLoader position="centerOnParent" />;
    }

    if (!project) {
        return <PageNotFound />;
    }

    const handleTabChange = (tab: ProjectTabType) => {
        setActiveTab(tab);
    };

    const handleDeleteClick = () => {
        setIsDeleteModalOpen(true);
    };

    const handleDeleteConfirm = () => {
        execDelete(projectId);
        setIsDeleteModalOpen(false);
    };

    const handleDeleteCancel = () => {
        setIsDeleteModalOpen(false);
    };

    const handleArchiveConfirm = () => {
        const action = project?.isArchived ? unarchiveProject : archiveProject;
        action(projectId, () => {
            setIsArchiveModalOpen(false);
            getProjects(undefined, true);
        });
    };

    const renderTabContent = () => {
        switch (activeTab) {
            case 'overview':
                return (
                    <ProjectOverviewTab
                        project={project as ProjectWithAssigneesDto}
                        onRefresh={refreshProject}
                        canEdit={canEdit}
                        showRates={canEdit}
                        showOnlyOwnData={showOnlyOwnData}
                        currentUserSub={authUser.userSub}
                        currency={project.currency}
                    />
                );
            case 'files':
                return ownerTabs ? <ProjectFilesTab projectId={project.id} /> : null;
            case 'audit-trail':
                return ownerTabs ? <ProjectAuditTrailTab projectId={project.id} /> : null;
            case 'timesheet':
                return (
                    <ProjectTimesheetTab
                        project={project as ProjectWithAssigneesDto}
                        showOnlyOwnData={showOnlyOwnData}
                        currentUserSub={authUser.userSub}
                        showRates={canEdit}
                        canManageSubmissions={canManageSubmissions}
                        currentUserName={currentUserMemberData.name}
                        currentUserRole={currentUserMemberData.role}
                        currentUserImageUrl={currentUserMemberData.imageUrl}
                        currentUserRate={currentUserMemberData.rate}
                    />
                );
        }
    };

    return (
        <main className="project-page">
            <ProjectHeader
                projectName={project.name}
                clientName={project.clientName || ''}
                projectStatus={project.projectStatus}
                projectId={project.id}
                clientId={project.clientId}
                canEdit={canEdit}
                onDeleteClick={handleDeleteClick}
                onArchiveToggleClick={() => setIsArchiveModalOpen(true)}
                archivedMode={!!project.isArchived}
                onStatusBadgeClick={() => setIsChangeStatusOpen(true)}
            />

            {canSeeAllTabs && <ProjectTabs activeTab={activeTab} onTabChange={handleTabChange} visibleTabs={visibleTabs} />}

            <div className="project-page__content">{renderTabContent()}</div>

            <Modal isVisible={isDeleteModalOpen} closeModal={handleDeleteCancel}>
                <YesNo
                    title="Delete Project"
                    description={`Are you sure you want to delete the project "${project.name}"? This action cannot be undone.`}
                    onYes={handleDeleteConfirm}
                    onNo={handleDeleteCancel}
                />
            </Modal>
            <Modal isVisible={isArchiveModalOpen} closeModal={() => setIsArchiveModalOpen(false)}>
                <YesNo
                    title={project.isArchived ? t('restoreProject') : t('archiveProject')}
                    description={
                        project.isArchived
                            ? t('restoreProjectDescription', { projectName: project.name })
                            : t('archiveProjectDescription', { projectName: project.name })
                    }
                    onYes={handleArchiveConfirm}
                    onNo={() => setIsArchiveModalOpen(false)}
                />
            </Modal>
            <Modal isVisible={isChangeStatusOpen} closeModal={() => setIsChangeStatusOpen(false)}>
                <ChangeProjectStatus
                    projectId={project.id}
                    currentStatus={project.projectStatus as ProjectStatus}
                    currentEndDate={project.endDate}
                    close={() => setIsChangeStatusOpen(false)}
                    onSuccess={refreshProject}
                />
            </Modal>
        </main>
    );
};

const ProjectPageWrapper = () => {
    const navigate = useNavigate();
    const isMobile = useResizeListener().isMaxWidthLarge;

    const { id } = useParams();
    const { t: mobileT } = useTranslation('notAvailableOnMobile');

    if (isMobile) {
        return (
            <NotAvailableOnMobile
                title={mobileT('title')}
                paragraph={mobileT('paragraph')}
                buttonText={mobileT('button')}
                onClick={() => navigate('/')}
            />
        );
    }

    if (!id) {
        return <PageNotFound />;
    }

    return <ProjectPage projectId={id} />;
};

export default ProjectPageWrapper;
